import React, { Component } from "reactn";

import {
    DialogTitle,
    DialogActions,
    Button,
    LinearProgress,
    TextField
} from "@material-ui/core";

import { _ } from "js/vendor";
import isConnected from "js/core/utilities/isConnected";
import { getImageType, isValidMediaType } from "js/core/utilities/imageUtilities";
import { TaskState, TaskType, AssetType, ImageErrorType } from "common/constants";
import { RowGrid } from "js/react/components/LayoutGrid";
import { BeautifulDialog, DialogContent, ShowOfflineDialog, ShowUpgradeDialog, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import ProBadge from "js/react/components/ProBadge";
import ErrorPane from "js/react/components/ErrorPane";
import shouldShowProBadge from "js/editor/shouldShowProBadge";
import { getStaticUrl } from "js/config";
import { FeatureType } from "js/core/models/features";
import { UpgradePlanDialogType } from "js/react/views/MarketingDialogs/UpgradePlanDialog";
import { trackActivity } from "js/core/utilities/utilities";
import { ds } from "js/core/models/dataService";
import { uploadFileAndCreateTask } from "js/core/services/tasks";
import log from "js/core/logger";
import * as geom from "js/core/utilities/geom";
import Api from "js/core/api";
import { UIController } from "js/editor/dialogs/UIController";
import Dropbox from "js/core/utilities/dropbox";
import { Gap10 } from "js/react/components/Gap";

export class ImportMediaDialog extends Component {
    constructor(props) {
        super(props);

        this.uploadRef = React.createRef();

        this.getVideoAssetDebounced = _.debounce(this.getVideoAsset, 500);
    }

    state = {
        asset: null,
        uiState: "choose",
        urlText: "",
        uploadState: TaskState.PREPARING,
        isUploading: false,
        progress: 0,
        label: "",
        error: null,
    };

    render() {
        const {
            urlText,
            label,
            progress,
            uiState,
            uploadState,
            asset,
            error,
        } = this.state;
        const {
            closeDialog,
            backgroundVideoOnly = false,
            videoOnly = false,
            imageOnly = false,
        } = this.props;
        const uploadDisabled = shouldShowProBadge(FeatureType.VIDEO_UPLOAD, UIController.getWorkspaceId());
        const allowVideo = videoOnly || !imageOnly;
        const allowImage = imageOnly || !videoOnly;
        switch (uiState) {
            case "choose":
                return (
                    <BeautifulDialog closeDialog={closeDialog} maxWidth="md" fullWidth={false} closeButton>
                        {
                            videoOnly &&
                            <DialogTitle>Import Video</DialogTitle>
                        }
                        {
                            !videoOnly &&
                            <DialogTitle>Import Media</DialogTitle>
                        }
                        <DialogContent className="import-media-dialog">
                            <div>
                                <span className="instruction">
                                    {allowVideo && "Import a video"}
                                    <ProBadge
                                        show={uploadDisabled}
                                        analytics={{ cta: "MediaUpload", ...(ds.selection.presentation ? ds.selection.presentation.getAnalytics() : {}) }}
                                        workspaceId={UIController.getWorkspaceId()}
                                    />
                                    {!allowVideo && "Import an image or gif"}
                                    {allowVideo && allowImage && ", image, or gif"}:
                                </span>
                            </div>
                            <div
                                className="media-option drop-zone"
                                onClick={() => this.handleMediaOptionClick("upload")}
                                onDragEnter={evt => evt.preventDefault()}
                                onDragOver={evt => evt.preventDefault()}
                                onDragLeave={evt => evt.preventDefault()}
                                onDrop={evt => this.handleMediaOptionClick("upload", evt)}
                            >
                                <input
                                    ref={this.uploadRef}
                                    type="file"
                                    style={{ display: "none" }}
                                    accept={
                                        videoOnly ? "video/*"
                                            : imageOnly ? "image/*"
                                                : "image/*, video/*"
                                    }
                                    onChange={this.handleFileSelectChange}
                                />
                                <i className="logo micon">cloud_upload</i>
                                <label>Drag and drop</label>
                                <span className="text-blue"><span style={{ color: "black" }}>or</span> browse files...</span>
                            </div>
                            <div>
                                <span>Or select files from:</span>
                            </div>
                            <div style={{ display: "flex" }}>
                                <div
                                    className="media-option"
                                    onClick={this.handleDropboxClick}
                                >
                                    <img className="logo no-scale" src={getStaticUrl("/images/file_hosting/dropbox.svg")} />
                                    <label>Dropbox</label>
                                </div>
                                {!backgroundVideoOnly && (
                                    <>
                                        <div
                                            className="media-option"
                                            onClick={() => this.handleMediaOptionClick("vimeo")}
                                        >
                                            <img className="logo" src={getStaticUrl("/images/ui/vendor/vimeo-logo.png")} />
                                            <label>Vimeo</label>
                                        </div>
                                        <div
                                            className="media-option"
                                            onClick={() => this.handleMediaOptionClick("youtube")}
                                        >
                                            <img className="logo" src={getStaticUrl("/images/ui/vendor/youtube-logo.png")} />
                                            <label>YouTube</label>
                                        </div>
                                    </>
                                )}
                            </div>
                        </DialogContent>
                    </BeautifulDialog>
                );
            case "video-url":
                return (
                    <BeautifulDialog closeDialog={closeDialog} maxWidth="md" fullWidth={true}>
                        <DialogTitle>Import Video</DialogTitle>
                        <DialogContent className="import-media-dialog">
                            <div>
                                <TextField
                                    type="url"
                                    pattern="https://.*"
                                    required
                                    placeholder="Paste the url of a Vimeo or YouTube video..."
                                    value={urlText}
                                    fullWidth
                                    autoFocus
                                    variant="outlined"
                                    onChange={event => {
                                        const urlText = event.target.value;
                                        const error = event.target.validationMessage;
                                        this.setState({
                                            urlText,
                                            error,
                                        });

                                        // If there are no validation errors, request the asset
                                        if (!error) {
                                            this.getVideoAssetDebounced(urlText);
                                        }
                                    }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                />
                            </div>
                            {
                                error &&
                                <ErrorPane>{error}</ErrorPane>
                            }
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={closeDialog}>Cancel</Button>
                            <Button color="primary" disabled={!asset?.url} onClick={this.onConfirm}>Add Video</Button>
                        </DialogActions>
                    </BeautifulDialog>
                );
            case "media-upload":
                return (
                    <BeautifulDialog closeDialog={closeDialog} maxWidth="md" fullWidth={true} preventClose>
                        {uploadState === TaskState.PREPARING && <DialogTitle>Uploading Media</DialogTitle>}
                        {uploadState === TaskState.PROCESSING && <DialogTitle>Processing Media</DialogTitle>}
                        {uploadState === TaskState.ERROR && <DialogTitle>Error</DialogTitle>}
                        {uploadState === TaskState.FINISHED && <DialogTitle>Your Media Is Ready</DialogTitle>}
                        <DialogContent className="import-media-dialog">
                            <RowGrid item xs={12}>
                                <div style={{ width: "100%" }}>{label}</div>
                            </RowGrid>
                            <Gap10 />
                            <RowGrid item xs={12}>
                                <LinearProgress
                                    value={progress}
                                    variant="determinate"
                                />
                            </RowGrid>
                            {uploadState === TaskState.PREPARING &&
                                <RowGrid item xs={12}>
                                    <div style={{ fontStyle: "italic", fontWeight: 500, fontSize: 16, color: "#666", paddingTop: 20 }}>Hang tight. This might take a minute or so. Please do not close or refresh the browser while your media is uploading.</div>
                                </RowGrid>}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={closeDialog}>Cancel</Button>
                            <Button color="primary" disabled={!asset} onClick={this.onConfirm}>Add Media</Button>
                        </DialogActions>
                    </BeautifulDialog>
                );
        }
    }

    onConfirm = async () => {
        const { asset } = this.state;

        this.props.onAccept(asset);
        this.props.closeDialog();
    }

    getVideoAsset = async (hostedVideoUrl, videoAssetId) => {
        let error;

        const isUpload = !!videoAssetId;
        const isYoutube = !!hostedVideoUrl && (hostedVideoUrl.includes("youtube") || hostedVideoUrl.includes("youtu.be"));
        const isVimeo = !!hostedVideoUrl && hostedVideoUrl.includes("vimeo");
        const videoType = isUpload ? "upload" : (isVimeo ? "vimeo" : (isYoutube ? "youtube" : hostedVideoUrl));

        const {
            backgroundVideoOnly = false,
        } = this.props;

        // Default the video size
        let size = new geom.Size(800, 450);
        let duration = 100;

        const assetAttributes = {};

        log.info("ImportMediaDialog: Checking new video dimensions...");
        if (isUpload) {
            try {
                const assetModel = await ds.assets.getAssetById(videoAssetId, "video");
                await assetModel.load();
                Object.assign(assetAttributes, assetModel.attributes);
                size = new geom.Size(assetAttributes.w, assetAttributes.h);
                duration = assetAttributes.duration;
            } catch (err) {
                log.error(err);
                error = err.message;
            }
        } else {
            try {
                const oembedUrl = isVimeo
                    ? `https://www.vimeo.com/api/oembed.json?url=${encodeURIComponent(hostedVideoUrl)}&byline=false&portrait=false&title=false`
                    : `https://www.youtube.com/oembed?url=${encodeURIComponent(hostedVideoUrl)}&format=json`;
                const response = await Api.oembed.post({ url: oembedUrl });
                size = new geom.Size(response.width, response.height);
                duration = response.duration || duration;
                assetAttributes.url = hostedVideoUrl;
            } catch (err) {
                // Since we are processing a user supplied url, we're going to display errors as warnings
                log.warn(err);
                if (err.message === "Bad oembed request: Bad Request") {
                    error = "Invalid video url";
                } else {
                    error = err.message;
                }
            }
        }

        if (error) {
            this.setState({
                asset: null,
                error
            });
            return;
        }

        trackActivity("Video", "AddedToSlide", null, null, {
            "slide_id": ds.selection.slide?.id,
            "video_type": videoType,
            "url": hostedVideoUrl,
            "id": videoAssetId,
        }, { audit: true });

        const controls = !backgroundVideoOnly && duration > 30;

        this.setState({
            asset: {
                hostedVideoUrl,
                id: videoAssetId,
                type: AssetType.VIDEO,
                size,
                assetProps: {
                    muted: true,
                    duration,
                    startTime: 0,
                    endTime: duration,
                    autoPlay: !controls,
                    loop: !controls,
                    speed: 1.0,
                    controls,
                },
                ...assetAttributes,
            },
            error: null,
        });
    }

    checkFileSize = file => {
        const maxFileSizeBytes = 500 * 1024 * 1024; // 500 MB
        if (file.size > maxFileSizeBytes) {
            return false;
        }

        return true;
    }

    handleFileSelectChange = event => {
        const files = event.target?.files || event.dataTransfer?.files;
        const file = files[0];
        if (this.checkFileSize(event)) {
            this.uploadFile(file);
        } else {
            this.setState({ label: "File is too large to upload", uiState: "media-upload" });
            event.target.value = null;
        }
    }

    handleDropboxClick = async event => {
        try {
            const res = await Dropbox.chooseFile([".jpg", ".jpeg", ".png", ".svg", ".gif"]);
            if (!res.cancelled) {
                this.uploadFile(res);
            }
        } catch (err) {
            log.error(err);
            ShowErrorDialog({
                error: "Sorry, we could not process your request",
                message: err.message
            });
        }
    }

    uploadFile = file => {
        if (!isConnected.connected) {
            ShowOfflineDialog();
            return null;
        }

        if (!isValidMediaType(file)) {
            this.setState({
                progress: 0,
                uploadState: TaskState.ERROR,
                label: "Media format not supported. Only image formats JPEG, PNG, GIF, SVG and most video formats are permitted."
            });
            return null;
        }

        if (file.type.includes("image/")) {
            const { imageAsLogo, overwriteAssetId } = this.props;

            const fileName = file.name;

            const props = {
                "slide_id": ds.selection.slide?.id,
                "uploaded_from": file.source ? file.source : "local",
            };
            if (imageAsLogo) {
                trackActivity("Logo", "Upload", fileName, null, props, { audit: true });
            } else {
                trackActivity("Image", "Upload", fileName, null, props, { audit: true });
            }

            let fileType = getImageType(file);
            const imageProperties = {};
            if (imageAsLogo) {
                // force all logos have solid white backgrounds
                imageProperties.hasSolidBackground = true;
                imageProperties.imageBackgroundColor = "#FFFFFF";
            }

            this.setState({
                label: file.name,
                uiState: "media-upload"
            });

            ds.assets
                .getOrCreateImage({
                    file,
                    url: file.url || null,
                    name: fileName,
                    fileType,
                    assetType: imageAsLogo ? AssetType.LOGO : AssetType.IMAGE,
                    metadata: {
                        source: "upload"
                    },
                    ...[{ ...imageProperties }],
                    overwriteAssetId
                })
                .then(asset => {
                    this.setState({
                        progress: 100,
                        uploadState: TaskState.FINISHED,
                        asset: {
                            type: asset.type,
                            id: asset.id,
                            url: asset.getUrl && asset.getUrl(),
                            size: new geom.Size(asset.get("w"), asset.get("h")),
                            ...asset,
                        },
                    });
                    this.onConfirm();
                })
                .catch(err => {
                    let label;
                    if (err.type === ImageErrorType.SIZE) {
                        label = err.message;
                    } else if (err.type === ImageErrorType.BAD_DATA) {
                        label = "We were unable to upload this image. Please try again.";
                    } else {
                        label = "We are unable to process and read this image.";
                    }
                    this.setState({
                        progress: 0,
                        uploadState: TaskState.ERROR,
                        label,
                    });
                    log.error(err);
                });
        } else if (file.type.includes("video/")) {
            const uploadDisabled = shouldShowProBadge(FeatureType.VIDEO_UPLOAD, UIController.getWorkspaceId());
            if (uploadDisabled) {
                ShowUpgradeDialog({
                    type: UpgradePlanDialogType.UPGRADE_PLAN,
                    analytics: { cta: "MediaUpload", ...(ds.selection.presentation?.getAnalytics() ?? {}) },
                    workspaceId: UIController.getWorkspaceId()
                });
            } else {
                this.setState({
                    label: file.name,
                    uiState: "media-upload"
                });

                uploadFileAndCreateTask(file, TaskType.VIDEO_UPLOAD, async task => {
                    if (task.state === TaskState.ERROR) {
                        this.setState({
                            progress: 0,
                            uploadState: TaskState.ERROR,
                            label: task.errorMessage
                        });
                        return;
                    }

                    if (task.state === TaskState.PREPARING) {
                        this.setState({
                            progress: task.stateProgressPercents / 2,
                            uploadState: TaskState.PREPARING
                        });
                        return;
                    }

                    if (task.state === TaskState.PROCESSING) {
                        this.setState({
                            progress: task.stateProgressPercents / 2 + 50,
                            uploadState: TaskState.PROCESSING
                        });
                        return;
                    }

                    if (task.state === TaskState.FINISHED) {
                        this.setState({
                            progress: 100,
                            uploadState: TaskState.FINISHED
                        }, () => this.getVideoAsset(null, task.videoAssetId));
                    }
                }, undefined, this.props.overwriteAssetId);
            }
        }
    }

    handleMediaOptionClick = (type, event = null) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        if (type == "upload") {
            if (event) {
                this.handleFileSelectChange(event);
            } else {
                this.uploadRef.current.click();
            }
        } else {
            this.setState({ uiState: "video-url" });
        }
    }
}

